import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from "react-router";
import { Button, DatePicker, Input, Modal, Select } from 'antd';
import styled from "styled-components";
import { Moment } from 'moment';

import ko from 'antd/es/date-picker/locale/ko_KR';

import userApi from "../../users/services/users.service";
import { useAuthContext } from '../../auth/context/useAuthContext';
import { AUTO_SET_COUNT_COUPONS, AUTO_SET_COUPONS, COUPON_CONFIGS, normalCouponOptions, partnerCouponOptions, vehicleOptions } from '../constants/coupon';
import { CouponType, CouponValue, PlatformType, VehicleType } from '../types/coupon';


const { Option } = Select;

interface ModalState {
  isVisible: boolean;
  couponKind: 'normal' | 'partner' | '';
}

interface CouponAddModalProps {
  setModalState: (state: ModalState) => void;
  modalState: ModalState;
  setForceReload: () => void;
}

export function CouponAddModal({ setModalState, modalState, setForceReload }: CouponAddModalProps) {
  const { auth } = useAuthContext();
  const { userId } = useParams<{ userId: string }>();

  const [couponType, setCouponType] = useState<CouponType>('');
  const [couponValue, setCouponValue] = useState<CouponValue>({} as CouponValue);
  const [count, setCount] = useState<number>(1);
  const [expiryDate, setExpiryDate] = useState<Moment | null>(null);
  const [availableVehicles, setAvailableVehicles] = useState<VehicleType>('');
  const [platform, setPlatform] = useState<PlatformType>('SWING');

  const hideModal = useCallback(() => {
    setCouponType('');
    setCouponValue({} as CouponValue);
    setCount(0);
    setExpiryDate(null);
    setAvailableVehicles('');
    setPlatform('SWING');
    setModalState({
      isVisible: false,
      couponKind: ''
    });
  }, [setModalState]);

  // 모달 종류에 따른 초기 쿠폰 타입 설정
  useEffect(() => {
    if (modalState.couponKind === "normal") {
      setCouponType("0");
    } else if (modalState.couponKind === "partner") {
      setCouponType("5");
    }
  }, [modalState.couponKind]);

  // 쿠폰 타입별 설정 적용
  useEffect(() => {
    if (!couponType || !auth?.email) return;

    const config = COUPON_CONFIGS[couponType as Exclude<CouponType, ''>];
    if (!config) return;

    const newCouponValue: CouponValue = {
      ...config,
      issuer: auth.email,
    };

    setCouponValue(newCouponValue);

    // 자동 설정 쿠폰들의 경우 만료일과 개수 자동 설정
    if (AUTO_SET_COUPONS.includes(couponType)) {
      setExpiryDate(null);
      setCount(1);
    }
  }, [couponType, auth?.email]);

  const handleCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCount(Number(value));
  }, []);

  const validateCouponData = (): string | null => {
    if (!availableVehicles) return "이용 가능 기종을 선택해주세요!";

    if (!AUTO_SET_COUPONS.includes(couponType)) {
      if (!expiryDate) return "만료 기한을 설정해주세요!";
      if (count <= 0) return "쿠폰은 1개 이상부터 발급 가능합니다.";
      if (count > 20) return "쿠폰은 20개 까지만 발급 가능합니다.";
    }

    return null;
  };

  const handleClickApply = useCallback(async () => {
    if (!userId) return alert("사용자 ID가 없습니다.");

    const validationError = validateCouponData();
    if (validationError) return alert(validationError);

    try {
      const requestData: CouponValue = {
        ...couponValue,
        couponVehicleSType: availableVehicles,
        platform,
      };

      // 자동 설정 쿠폰이 아닌 경우 만료일 설정
      if (!AUTO_SET_COUPONS.includes(couponType) && expiryDate) {
        requestData.expiryDate = expiryDate.endOf("day");
      }

      // 쿠폰 발급
      await Promise.all(
        Array(count)
          .fill(0)
          .map(() => userApi.postUserCoupon(userId, requestData))
      );

      setForceReload();
      hideModal();
    } catch (error) {
      console.error('쿠폰 발급 중 오류:', error);
      alert('쿠폰 발급 중 오류가 발생했습니다.');
    }
  }, [userId, couponValue, availableVehicles, platform, couponType, expiryDate, count, validateCouponData, setForceReload, hideModal]);

  const isAutoSetCoupon = AUTO_SET_COUPONS.includes(couponType);
  const isAutoSetCountCoupon = AUTO_SET_COUNT_COUPONS.includes(couponType);
  const couponOptions = modalState.couponKind === 'normal' ? normalCouponOptions : partnerCouponOptions;

  return (
    <Modal
      title="쿠폰 발급"
      visible={modalState.isVisible}
      onCancel={hideModal}
      footer={null}
      forceRender
    >
      <FormContainer>
        <InputWrapper>
          <Label>쿠폰 종류:</Label>
          <Select
            value={couponType}
            style={{ width: 160 }}
            onChange={setCouponType}
            placeholder="쿠폰 종류를 선택하세요"
          >
            {couponOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </InputWrapper>

        <InputWrapper>
          <Label>만료 기한:</Label>
          <DatePicker
            locale={ko}
            style={{ width: 160 }}
            value={expiryDate}
            disabled={isAutoSetCoupon}
            onFocus={(e) => e.target.blur()}
            onChange={setExpiryDate}
            placeholder="만료 기한을 선택하세요"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>발급 개수:</Label>
          <Input
            style={{ width: 160 }}
            value={count}
            disabled={isAutoSetCoupon || isAutoSetCountCoupon}
            onChange={handleCountChange}
            placeholder="발급할 개수를 입력하세요"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>이용 앱:</Label>
          <Select
            value={platform}
            style={{ width: 160 }}
            onChange={setPlatform}
          >
            <Option value="SWING">SWING</Option>
            <Option value="DEER">DEER</Option>
          </Select>
        </InputWrapper>

        <InputWrapper>
          <Label>사용 가능 기종:</Label>
          <Select
            value={availableVehicles}
            style={{ width: 160 }}
            onChange={setAvailableVehicles}
            placeholder="기종을 선택하세요"
          >
            {vehicleOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </InputWrapper>

        <Button
          type="primary"
          onClick={handleClickApply}
          style={{ marginTop: 16 }}
          block
        >
          발급
        </Button>
      </FormContainer>
    </Modal>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin: 8px 0;
  align-items: center;
  width: 100%;
  max-width: 280px;
`;

const Label = styled.p`
  margin: 0 16px 0 0;
  width: 100px;
  font-weight: 500;
  color: #333;
`;
